"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useEffect } from "react";
import {
  Search,
  SlidersHorizontal,
  X,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Check
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// CustomCheckbox component for premium custom checklist visuals
function CustomCheckbox({ checked, onChange, label, count }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className="flex items-center justify-between w-full text-right py-2.5 px-3 rounded-xl hover:bg-gray-50 transition-all duration-200 group cursor-pointer"
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-5 h-5 rounded-lg border flex items-center justify-center transition-all duration-200 ${checked
            ? "bg-blue-950 border-blue-950 text-white shadow-sm shadow-blue-950/25 scale-105"
            : "border-gray-200 bg-white group-hover:border-gray-300"
            }`}
        >
          {checked && <Check className="w-3.5 h-3.5 stroke-[3] animate-scale-in" />}
        </div>
        <span className={`text-sm transition-colors duration-200 ${checked
          ? "text-gray-900 font-semibold"
          : "text-gray-600 font-medium group-hover:text-gray-800"
          }`}>
          {label}
        </span>
      </div>
      {count !== undefined && (
        <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium transition-all duration-200 latinNumbers ${checked
          ? "bg-blue-50 text-blue-950 font-semibold"
          : "bg-gray-100 text-gray-400 group-hover:bg-gray-200 group-hover:text-gray-600"
          }`}>
          {count}
        </span>
      )}
    </button>
  );
}

// Collapsible Group component with framer-motion height transition
function FilterGroup({ title, children, isOpen, onToggle }) {
  return (
    <div className="border-b border-gray-100 py-4.5 last:border-0">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center justify-between w-full text-right font-bold text-gray-800 pb-2.5 cursor-pointer hover:text-blue-950 transition-colors"
      >
        <span className="text-base">{title}</span>
        <div className={`p-1 rounded-lg bg-gray-50 text-gray-400 hover:text-gray-600 transition-all duration-200 ${isOpen ? "rotate-180 bg-blue-50 text-blue-950" : ""
          }`}>
          <ChevronDown className="w-4 h-4" />
        </div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="pt-2 space-y-1">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function ProductClient({ serverProducts }) {
  // Initialize state with the server data
  const [products] = useState(serverProducts);

  // Filter States
  const [filters, setFilters] = useState({
    trim: [],
    brand: [],
    power: [],
    year: [],
    differential: [],
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const trimParam = params.get("trim");
      if (trimParam) {
        let selected = trimParam;
        if (trimParam.includes("شاسی")) selected = "شاسی بلند";
        else if (trimParam.includes("سدان")) selected = "سدان";
        else if (trimParam.includes("کراس")) selected = "کراس اور";
        else if (trimParam.includes("برقی")) selected = "برقی";

        setFilters((prev) => ({
          ...prev,
          trim: [selected],
        }));
      }
    }
  }, []);

  // Add new states for responsive search & interactive drawer
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({
    trim: true,
    brand: true,
    power: true,
    differential: true,
    year: true,
  });

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const normalizeTrim = (rawTrim) => {
    if (!rawTrim) return "";
    const str = rawTrim.toString().trim().replace(/[\u200c\u200b]/g, " ").replace(/\s+/g, " ");

    if (str.includes("شاسی")) return "شاسی بلند";
    if (str.includes("کراس")) return "کراس اور";
    if (str.includes("سدان")) return "سدان";
    if (str.includes("برقی") || /ev|electric/i.test(str)) return "برقی";

    return str;
  };

  // Generate filter options dynamically based on the fetched data
  const generateFilterOptions = (category) => {
    const uniqueValues = [...new Set(products.map((item) => item[category]))];
    return uniqueValues;
  };

  const generateBrandFilterOptions = () => {
    const uniqueBrands = [...new Set(products.map((item) => item.car_model.brand.name))];
    return uniqueBrands;
  };

  const generateTrimFilterOptions = () => {
    const canonical = ["سدان", "شاسی بلند", "کراس اور", "برقی"];
    const extraTrims = products
      .map((item) => normalizeTrim(item.trim))
      .filter((t) => Boolean(t) && !canonical.includes(t));
    return [...new Set([...canonical, ...extraTrims])];
  };

  // Generate the dynamic drivetype options
  const generateDifferentialOptions = () => {
    const uniqueDifferentials = [...new Set(products.map((item) => item.technical_info.drivetype))];
    return uniqueDifferentials.filter((drivetype) => drivetype !== null); // Filter out null values
  };

  // Filtered products based on selected filters and search query
  const filteredProducts = products.filter(item => {
    const matchesSearch = searchQuery.trim() === "" ||
      item.english_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.car_model.brand.name.toLowerCase().includes(searchQuery.toLowerCase());

    const isBrandMatch = filters.brand.length === 0 || filters.brand.includes(item.car_model.brand.name);
    const isPowerMatch = filters.power.length === 0 || filters.power.includes(item.fuel_type);
    const isYearMatch = filters.year.length === 0 || filters.year.includes(item.production_year.toString());
    const isDifferentialMatch = filters.differential.length === 0 || filters.differential.includes(item.technical_info.drivetype);

    const isTrimMatch = filters.trim.length === 0 || filters.trim.some(t => {
      const targetNorm = normalizeTrim(t);
      const itemTrimNorm = normalizeTrim(item.trim);
      const itemFuelNorm = normalizeTrim(item.fuel_type);

      if (targetNorm === "برقی") {
        return itemTrimNorm === "برقی" || itemFuelNorm === "برقی" || (item.fuel_type && /ev|electric/i.test(item.fuel_type));
      }
      return itemTrimNorm === targetNorm;
    });

    return matchesSearch && isBrandMatch && isPowerMatch && isYearMatch && isDifferentialMatch && isTrimMatch;
  });

  // Handle filter changes (checkbox logic)
  const handleCheckboxChange = (category, value) => {
    setFilters(prevFilters => {
      const newCategory = prevFilters[category].includes(value)
        ? prevFilters[category].filter(item => item !== value)
        : [...prevFilters[category], value];

      return { ...prevFilters, [category]: newCategory };
    });
  };

  // Clear all filters
  const handleClearAll = () => {
    setFilters({
      trim: [],
      brand: [],
      power: [],
      year: [],
      differential: [],
    });
    setSearchQuery("");
  };

  const getTrimCount = (trimName) => {
    const targetNorm = normalizeTrim(trimName);
    return products.filter((item) => {
      const itemTrimNorm = normalizeTrim(item.trim);
      const itemFuelNorm = normalizeTrim(item.fuel_type);

      if (targetNorm === "برقی") {
        return itemTrimNorm === "برقی" || itemFuelNorm === "برقی" || (item.fuel_type && /ev|electric/i.test(item.fuel_type));
      }
      return itemTrimNorm === targetNorm;
    }).length;
  };

  const getBrandCount = (brandName) => {
    return products.filter((item) => item.car_model.brand.name === brandName).length;
  };

  const getFuelCount = (fuelType) => {
    return products.filter((item) => item.fuel_type === fuelType).length;
  };

  const getDifferentialCount = (drivetype) => {
    return products.filter((item) => item.technical_info.drivetype === drivetype).length;
  };

  const getYearCount = (year) => {
    return products.filter((item) => item.production_year === year).length;
  };

  // Create list of active chips
  const activeChips = [
    ...filters.trim.map(val => ({ category: "trim", val, label: val })),
    ...filters.brand.map(val => ({ category: "brand", val, label: val })),
    ...filters.power.map(val => ({ category: "power", val, label: val })),
    ...filters.differential.map(val => ({ category: "differential", val, label: val })),
    ...filters.year.map(val => ({ category: "year", val, label: val.toString() })),
  ];

  // Common filter panels block to render in sidebar / mobile drawer
  const FilterContent = () => (
    <div className="space-y-2">
      <FilterGroup
        title="کلاس خودرو"
        isOpen={expandedGroups.trim}
        onToggle={() => toggleGroup("trim")}
      >
        {generateTrimFilterOptions().map((trimName) => (
          <CustomCheckbox
            key={trimName}
            checked={filters.trim.includes(trimName)}
            onChange={() => handleCheckboxChange("trim", trimName)}
            label={trimName}
            count={getTrimCount(trimName)}
          />
        ))}
      </FilterGroup>

      <FilterGroup
        title="برند خودرو"
        isOpen={expandedGroups.brand}
        onToggle={() => toggleGroup("brand")}
      >
        {generateBrandFilterOptions().map((brand) => (
          <CustomCheckbox
            key={brand}
            checked={filters.brand.includes(brand)}
            onChange={() => handleCheckboxChange("brand", brand)}
            label={brand}
            count={getBrandCount(brand)}
          />
        ))}
      </FilterGroup>

      <FilterGroup
        title="نوع موتور"
        isOpen={expandedGroups.power}
        onToggle={() => toggleGroup("power")}
      >
        {generateFilterOptions("fuel_type").map((power) => (
          <CustomCheckbox
            key={power}
            checked={filters.power.includes(power)}
            onChange={() => handleCheckboxChange("power", power)}
            label={power}
            count={getFuelCount(power)}
          />
        ))}
      </FilterGroup>

      <FilterGroup
        title="محور محرک"
        isOpen={expandedGroups.differential}
        onToggle={() => toggleGroup("differential")}
      >
        {generateDifferentialOptions().map((drivetype) => (
          <CustomCheckbox
            key={drivetype}
            checked={filters.differential.includes(drivetype)}
            onChange={() => handleCheckboxChange("differential", drivetype)}
            label={drivetype}
            count={getDifferentialCount(drivetype)}
          />
        ))}
      </FilterGroup>

      <FilterGroup
        title="سال"
        isOpen={expandedGroups.year}
        onToggle={() => toggleGroup("year")}
      >
        {generateFilterOptions("production_year").map((year) => (
          <CustomCheckbox
            key={year}
            checked={filters.year.includes(year.toString())}
            onChange={() => handleCheckboxChange("year", year.toString())}
            label={year}
            count={getYearCount(year)}
          />
        ))}
      </FilterGroup>
    </div>
  );

  return (
    <section className="p-6 md:p-18 mt-10 max-w-8xl mx-auto w-full" dir="rtl">
      {/* Dynamic Title Header */}
      <div className="text-center mb-10">
        <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight mb-2">محصولات برساوش</h1>

      </div>

      {/* Modern Filter Toolbar: Search bar, Mobile Drawer Trigger, Count */}
      <div className="bg-white border border-gray-100 rounded-3xl md:rounded-2xl shadow-sm p-4 mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
        {/* Search Field */}
        <div className="relative w-full md:w-96">
          <input
            type="text"
            placeholder="جستجو در نام یا برند خودرو..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-md md:rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-950 focus:border-transparent text-sm transition-all text-right"
          />
          <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute left-9 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Info & Responsive Buttons */}
        <div className="flex items-center justify-between md:justify-end w-full md:w-auto gap-4">
          {/* Mobile Drawer Trigger (Hidden on Desktop) */}
          <button
            onClick={() => setIsMobileDrawerOpen(true)}
            className="lg:hidden flex items-center gap-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-800 font-bold py-2.5 px-4 rounded-md md:rounded-lg text-sm transition-all cursor-pointer shadow-sm hover:shadow"
          >
            <SlidersHorizontal className="w-4 h-4 text-blue-950" />
            <span>فیلترها</span>
            {activeChips.length > 0 && (
              <span className="bg-blue-950 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-bold">
                {activeChips.length}
              </span>
            )}
          </button>

          {/* Counts */}
          <div className="text-sm text-gray-500 font-medium">
            نمایش <span className="text-gray-900 font-bold latinNumbers">{filteredProducts.length}</span> خودرو از <span className="latinNumbers">{products.length}</span>
          </div>
        </div>
      </div>

      {/* Active filters bar */}
      {activeChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2.5 mb-6 p-4.5 bg-blue-50/20 border border-blue-100/50 rounded-3xl md:rounded-2xl">
          <span className="text-xs font-bold text-gray-400 ml-1.5">فیلترهای فعال:</span>
          {activeChips.map((chip, idx) => (
            <button
              key={idx}
              className="flex items-center gap-1.5 bg-white border border-blue-100 hover:border-red-200 text-blue-950 hover:text-red-600 py-1.5 px-3 rounded-md md:rounded-lg text-xs font-semibold shadow-xs transition-all cursor-pointer group"
              onClick={() => handleCheckboxChange(chip.category, chip.val)}
            >
              <span>{chip.label}</span>
              <X className="w-3.5 h-3.5 text-gray-400 group-hover:text-red-500 transition-colors" />
            </button>
          ))}
          <button
            onClick={handleClearAll}
            className="flex items-center gap-1.5 text-xs text-red-600 hover:text-red-700 font-bold mr-auto cursor-pointer"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            پاک کردن فیلترها
          </button>
        </div>
      )}

      {/* Main Grid: Sidebar + Products */}
      <div className="flex flex-col lg:flex-row gap-8 items-start">
        {/* Sticky Desktop Sidebar */}
        <aside className="hidden lg:block w-72 bg-white border border-gray-100 rounded-3xl md:rounded-2xl shadow-xs p-5 sticky top-24 max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-between pb-4.5 border-b border-gray-100 mb-3">
            <span className="font-extrabold text-gray-900 text-lg flex items-center gap-2">
              <SlidersHorizontal className="w-5 h-5 text-blue-950" />
              فیلترها
            </span>
            {activeChips.length > 0 && (
              <button
                onClick={handleClearAll}
                className="text-xs text-red-600 hover:text-red-700 font-bold transition-colors cursor-pointer"
              >
                پاک کردن همه
              </button>
            )}
          </div>
          <FilterContent />
        </aside>

        {/* Product Grid Area */}
        <div className="flex-1 w-full">
          {filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredProducts.map((item, i) => (
                <div
                  key={i}
                  className="flex flex-col bg-white border border-gray-100 rounded-[60px] md:rounded-[40px] overflow-hidden shadow-xs hover:shadow-md hover:border-gray-200/80 transition-all duration-300 group"
                >
                  {/* Aspect-ratio image wrapper with hover scale */}
                  <div className="relative aspect-[16/10] w-full bg-gray-50 overflow-hidden">
                    <Image
                      src={item.main_image || "/default-image.png"}
                      alt={`${item.english_name} ${item.production_year || ''}`}
                      fill
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      className="object-contain group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>

                  {/* Details */}
                  <div className="p-5 flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-start gap-2 mb-3">
                        <h3 className="text-lg font-bold text-gray-900 latinNumbers leading-tight">{item.english_name}</h3>
                        <span className="bg-gray-100 text-gray-700 text-xs font-bold px-2.5 py-1 rounded-lg shrink-0">
                          {item.car_model.brand.name}
                        </span>
                      </div>

                      {/* Specifications Grid */}
                      <div className="grid grid-cols-2 gap-y-3 gap-x-2 text-sm text-gray-600 border-t border-b border-gray-100 py-3.5 my-3.5">
                        <div className="flex items-center gap-1.5">
                          <span className="text-gray-400 font-semibold text-xs">سال ساخت:</span>
                          <span className="font-bold text-gray-800 latinNumbers">{item.production_year}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-gray-400 font-semibold text-xs">نوع موتور:</span>
                          <span className="font-bold text-gray-800 text-xs leading-none">{item.fuel_type}</span>
                        </div>
                        <div className="flex items-center gap-1.5 col-span-2">
                          <span className="text-gray-400 font-semibold text-xs">نیروی محرکه:</span>
                          <span className="font-bold text-gray-800 text-xs leading-none">{item.technical_info.drivetype || "N/A"}</span>
                        </div>
                      </div>
                    </div>

                    {/* Navigation/Action Buttons */}
                    <div className="mt-4 flex gap-3">
                      <Link
                        href={item.catalog_file || "#"}
                        className="flex-1 bg-blue-950 text-white py-2.5 px-4 rounded-md md:rounded-lg hover:bg-blue-900 transition-colors text-center text-sm font-bold shadow-xs hover:shadow shadow-blue-950/10"
                        target="_blank"
                      >
                        کاتالوگ
                      </Link>
                      <Link
                        href={`/products/${item.id}`}
                        className="flex-1 bg-gray-50 text-gray-800 py-2.5 px-4 rounded-md md:rounded-lg border border-gray-100/80 hover:bg-gray-100 hover:text-black transition-colors text-center text-sm font-bold"
                      >
                        جزئیات خودرو
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Premium Zero-Matches State */
            <div className="bg-white border border-gray-100 rounded-3xl md:rounded-2xl shadow-xs p-12 text-center max-w-md mx-auto mt-12">
              <div className="w-16 h-16 bg-blue-50 text-blue-950 rounded-3xl md:rounded-2xl flex items-center justify-center mx-auto mb-4.5 shadow-xs">
                <SlidersHorizontal className="w-7 h-7" />
              </div>
              <h3 className="text-lg font-extrabold text-gray-900 mb-1">خودرویی یافت نشد</h3>
              <p className="text-gray-500 text-sm mb-6 leading-relaxed">هیچ خودرویی با فیلترهای انتخابی شما مطابقت ندارد. لطفاً تنظیمات فیلتر را تغییر دهید یا دکمه بازنشانی را بزنید.</p>
              <button
                onClick={handleClearAll}
                className="inline-flex items-center gap-2 bg-blue-950 text-white px-5 py-2.5 rounded-md md:rounded-lg text-sm font-bold hover:bg-blue-900 transition-colors shadow-sm cursor-pointer"
              >
                <RotateCcw className="w-4 h-4" />
                پاک کردن فیلترها
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Responsive Filter Drawer for Mobile and Tablet */}
      <AnimatePresence>
        {isMobileDrawerOpen && (
          <>
            {/* Dark Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.4 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileDrawerOpen(false)}
              className="fixed inset-0 z-50 bg-black backdrop-blur-xs"
            />
            {/* Sheet Panel (Enforces RTL right-aligned slide-in) */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
              className="fixed inset-y-0 right-0 z-50 w-full max-w-sm bg-white shadow-2xl flex flex-col"
              dir="rtl"
            >
              {/* Header */}
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <span className="font-extrabold text-gray-900 text-lg flex items-center gap-2">
                  <SlidersHorizontal className="w-5 h-5 text-blue-950" />
                  فیلترها
                </span>
                <button
                  onClick={() => setIsMobileDrawerOpen(false)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Scrollable Filters */}
              <div className="flex-1 overflow-y-auto p-4">
                <FilterContent />
              </div>

              {/* Action Bar */}
              <div className="p-4 border-t border-gray-100 bg-gray-50/50 flex gap-3">
                <button
                  onClick={() => setIsMobileDrawerOpen(false)}
                  className="flex-1 bg-blue-950 text-white py-3 rounded-xl font-bold hover:bg-blue-900 transition-colors text-center text-sm shadow-sm cursor-pointer"
                >
                  اعمال فیلتر ({filteredProducts.length} خودرو)
                </button>
                {activeChips.length > 0 && (
                  <button
                    onClick={handleClearAll}
                    className="bg-white border border-gray-200 text-red-600 hover:bg-red-50 hover:border-red-300 py-3 px-4.5 rounded-xl font-bold transition-all text-sm cursor-pointer"
                  >
                    پاک کردن
                  </button>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </section>
  );
}